import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts'
import { TrendingUp, DollarSign, Activity, Users, Shield } from 'lucide-react'

const METRICS_CONFIG = [
  {
    key: 'demand',
    label: 'Demanda',
    icon: TrendingUp,
    description: 'Volumen de búsqueda y demanda activa del mercado',
    invertScore: false,
  },
  {
    key: 'margin',
    label: 'Margen',
    icon: DollarSign,
    description: 'Potencial de rentabilidad y márgenes estimados',
    invertScore: false,
  },
  {
    key: 'trend',
    label: 'Tendencia',
    icon: Activity,
    description: 'Dirección y momentum actual del mercado',
    invertScore: false,
  },
  {
    key: 'audience',
    label: 'Audiencia',
    icon: Users,
    description: 'Tamaño y accesibilidad del público objetivo',
    invertScore: false,
  },
  {
    key: 'competition',
    label: 'Competencia',
    icon: Shield,
    description: 'Nivel de saturación — menor valor es mejor',
    invertScore: true,
  },
]

function getScoreLabel(value, inverted) {
  const effective = inverted ? 100 - value : value
  if (effective >= 75) return { text: 'Excelente', color: '#d0d0d0' }
  if (effective >= 55) return { text: 'Bueno', color: '#a8a8a8' }
  if (effective >= 35) return { text: 'Moderado', color: '#7a7a7a' }
  return { text: 'Bajo', color: '#4a4a4a' }
}

function CircleRing({ value, size = 72 }) {
  const strokeWidth = 5
  const r = (size - strokeWidth * 2) / 2
  const circ = 2 * Math.PI * r
  const offset = circ - (value / 100) * circ
  const color = value >= 70 ? '#d0d0d0' : value >= 45 ? '#a8a8a8' : '#5a5a5a'

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {/* Track */}
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke="#2d2d2d"
        strokeWidth={strokeWidth}
      />
      {/* Progress */}
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 1.2s ease' }}
      />
    </svg>
  )
}

function MetricCard({ config, value }) {
  const Icon = config.icon
  const label = getScoreLabel(value, config.invertScore)

  return (
    <div className="relative flex flex-col items-center gap-3 p-4 bg-slate-darker border border-slate-dark/30 rounded-xl hover:border-slate-dark/70 transition-all group">
      {/* Top accent line */}
      <div
        className="absolute top-0 left-4 right-4 h-px rounded-full"
        style={{ background: `linear-gradient(90deg, transparent, ${label.color}40, transparent)` }}
      />

      {/* Icon */}
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-dark/60">
        <Icon className="w-4 h-4 text-[#94a3b8]er" />
      </div>

      {/* Circular ring with value */}
      <div className="relative flex items-center justify-center">
        <CircleRing value={value} size={72} />
        <span
          className="absolute text-lg font-bold tabular-nums"
          style={{ color: label.color }}
        >
          {value}
        </span>
      </div>

      {/* Label + status */}
      <div className="text-center">
        <p className="text-sm font-semibold text-[#94a3b8]est">{config.label}</p>
        <p className="text-xs mt-0.5" style={{ color: label.color }}>
          {label.text}
        </p>
      </div>
    </div>
  )
}

const CustomRadarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="px-3 py-2 bg-slate-darkest border border-slate-dark rounded-lg text-xs text-[#94a3b8]est shadow-lg">
      <p className="font-semibold">{payload[0]?.payload?.metric}</p>
      <p className="text-[#94a3b8]er">{payload[0]?.value}%</p>
    </div>
  )
}

export default function ComponentsAnalysis({ components }) {
  const metrics = METRICS_CONFIG.map(cfg => ({
    ...cfg,
    value: components[cfg.key] ?? 0,
  }))

  const strongest = metrics.filter(m => !m.invertScore).reduce((a, b) => a.value > b.value ? a : b)
  const weakest   = metrics.filter(m => !m.invertScore).reduce((a, b) => a.value < b.value ? a : b)
  const competition = metrics.find(m => m.key === 'competition')
  const competitionLabel = getScoreLabel(competition.value, true)

  const radarData = metrics.map(m => ({
    metric: m.label,
    value: m.invertScore ? 100 - m.value : m.value,
    fullMark: 100,
  }))

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white tracking-wide">Desglose del Análisis</h3>
          <p className="text-xs text-[#94a3b8] mt-0.5">Evaluación multidimensional del mercado</p>
        </div>
        <span className="text-xs px-2.5 py-1 bg-slate-darker border border-slate-dark/40 rounded-full text-[#94a3b8]er font-medium tracking-widest uppercase">
          5 factores
        </span>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {metrics.map(m => (
          <MetricCard key={m.key} config={m} value={m.value} />
        ))}
      </div>

      {/* Radar chart */}
      <div className="bg-slate-darker/50 border border-slate-dark/20 rounded-xl p-4">
        <p className="text-xs text-[#94a3b8] uppercase tracking-widest mb-3 font-medium">Perfil de mercado</p>
        <ResponsiveContainer width="100%" height={200}>
          <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <PolarGrid stroke="#3a3a3a" />
            <PolarAngleAxis
              dataKey="metric"
              tick={{ fill: '#a8a8a8', fontSize: 11 }}
            />
            <PolarRadiusAxis
              angle={30}
              domain={[0, 100]}
              tick={false}
              axisLine={false}
            />
            <Radar
              dataKey="value"
              stroke="#a8a8a8"
              fill="#a8a8a8"
              fillOpacity={0.12}
              strokeWidth={1.5}
            />
            <Tooltip content={<CustomRadarTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Interpretation */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 p-3 bg-slate-darker border border-slate-dark/20 rounded-lg">
          <p className="text-xs text-[#94a3b8] uppercase tracking-widest mb-1 font-medium">Punto más fuerte</p>
          <p className="text-sm text-[#94a3b8]est font-semibold">{strongest.label}</p>
          <p className="text-xs text-[#94a3b8] mt-0.5">{strongest.value}% — {strongest.description}</p>
        </div>
        <div className="flex-1 p-3 bg-slate-darker border border-slate-dark/20 rounded-lg">
          <p className="text-xs text-[#94a3b8] uppercase tracking-widest mb-1 font-medium">Área de atención</p>
          <p className="text-sm text-[#94a3b8]est font-semibold">{weakest.label}</p>
          <p className="text-xs text-[#94a3b8] mt-0.5">{weakest.value}% — Requiere estrategia adicional</p>
        </div>
        <div className="flex-1 p-3 bg-slate-darker border border-slate-dark/20 rounded-lg">
          <p className="text-xs text-[#94a3b8] uppercase tracking-widest mb-1 font-medium">Competencia</p>
          <p className="text-sm font-semibold" style={{ color: competitionLabel.color }}>{competitionLabel.text}</p>
          <p className="text-xs text-[#94a3b8] mt-0.5">{competition.value}% de saturación — {competition.value < 40 ? 'Mercado con espacio' : competition.value < 65 ? 'Competencia moderada' : 'Mercado muy competido'}</p>
        </div>
      </div>
    </div>
  )
}
